import { useState, useEffect, useCallback } from 'react'
import type { AuditEntry, AuditResource } from '../types'

interface UseAuditOptions {
  resource?: AuditResource
  limit?: number
  offset?: number
}

export function useAudit(options: UseAuditOptions = {}) {
  const [entries, setEntries] = useState<AuditEntry[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const fetchAudit = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const params = new URLSearchParams()
      if (options.resource) params.set('resource', options.resource)
      if (options.limit) params.set('limit', String(options.limit))
      if (options.offset) params.set('offset', String(options.offset))
      const qs = params.toString()
      const res = await fetch(`/api/audit${qs ? `?${qs}` : ''}`)
      if (!res.ok) throw new Error('Failed to fetch audit log')
      const data = await res.json()
      setEntries(data.entries || [])
      setTotal(data.total || 0)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      setLoading(false)
    }
  }, [options.resource, options.limit, options.offset])

  useEffect(() => {
    fetchAudit()
  }, [fetchAudit])

  return { entries, total, loading, error, refetch: fetchAudit }
}
